import _capitalize from "lodash/capitalize";
import _isNil from "lodash/isNil";
import _lowerCase from "lodash/lowerCase";
import { getAnnualFee, getAnnualFeeFirstYear, getIssuerName, getMaximumFICO, getMinimumFICO, getRewardRateType, getRewardType, getSignUpBonus, getSignUpBonusDetails, getSignUpBonusDetailsAnnotation, getSignUpBonusType } from '../selectors/credit-cards';
import { CREDIT_CARDS_REWARD_RATE_TYPES, CREDIT_CARDS_REWARD_TYPES, CREDIT_CARDS_REWARD_TYPE_DESCRIPTIONS } from '../constants';
/**
@enum BucketName
*/

export var BucketName = {
  user: 'user',
  poor: 'poor',
  average: 'average',
  good: 'good',
  excellent: 'excellent'
}; // This is where we define bounds for each bucket

export var ficoMap = {
  poor: {
    min: 300,
    max: 629
  },
  average: {
    min: 630,
    max: 689
  },
  good: {
    min: 690,
    max: 719
  },
  excellent: {
    min: 720,
    max: 850
  }
};
/**
Given a score, returns the bucket name the score falls into
@param {number} - credit score to be tested
@returns {BucketName} - name of the bucket which score falls into
*/

export var getBucketFromScore = function getBucketFromScore(score) {
  if (score >= ficoMap.excellent.min) {
    return BucketName.excellent;
  }

  if (score >= ficoMap.good.min) {
    return BucketName.good;
  }

  if (score >= ficoMap.average.min) {
    return BucketName.average;
  }

  return BucketName.poor;
};
/**
 * Get the associated bucket name for the maximum FICO score (eg. 800 => 'excellent').
 *
 * @param      {Object}  marketplaceEntity  The CC entity.
 * @return     {string}  The bucket name.
 */

export var getMaxCreditScoreBucket = function getMaxCreditScoreBucket(marketplaceEntity) {
  return getBucketFromScore(getMaximumFICO(marketplaceEntity));
};
/**
 * Get the associated bucket name for the minimum FICO score (eg. 800 => 'excellent').
 *
 * @param      {Object}  marketplaceEntity  The CC entity.
 * @return     {string}  The bucket name.
 */

export var getMinCreditScoreBucket = function getMinCreditScoreBucket(marketplaceEntity) {
  return getBucketFromScore(getMinimumFICO(marketplaceEntity));
};
/**
 * Returns true if the annual fee is waived for the first year, false otherwise.
 *
 * @param      {Object}  marketplaceEntity  The CC entity.
 * @return     {bool}    Whether or not the annual fee is waived for the first year.
 */

export var isAnnualFeeWaivedForFirstYear = function isAnnualFeeWaivedForFirstYear(marketplaceEntity) {
  return getAnnualFee(marketplaceEntity) > 0 && getAnnualFeeFirstYear(marketplaceEntity) === 0;
};
/**
 * Whether the offer is a discover.
 * @param {object} offer - The CC offer.
 * @returns {boolean}
 */

export var isDiscover = function isDiscover(offer) {
  return _lowerCase(getIssuerName(offer)) === 'discover';
};
/**
 * Whether the offer is a chase.
 * @param {object} offer - The CC offer.
 * @returns {boolean}
 */

export var isChase = function isChase(offer) {
  return _lowerCase(getIssuerName(offer)) === 'chase';
};
/**
 * Returns `value` field to be used in the Intro Offer Quick Fact.
 * @param {object} offer - The CC entity.
 * @returns {(string | number)} - The sign up bonus details annotation, or the sign up bonus value.
 */

export var getIntroOfferValue = function getIntroOfferValue(offer) {
  // For Discover cards - we use the "sign-up bonus details annotation" field
  if (isDiscover(offer)) {
    return getSignUpBonusDetailsAnnotation(offer);
  } // For Chase cards - we use the "sign-up bonus details annotation" field if it exists


  if (isChase(offer)) {
    var annotation = getSignUpBonusDetailsAnnotation(offer);
    if (annotation) return annotation;
  }

  var signUpBonus = getSignUpBonus(offer);
  var signUpBonusType = getSignUpBonusType(offer);
  /**
   * Display N/A only if the sign_up_bonus_type is null or "NO REWARDS"
   * and signup_bonus is 0, for all other sign_up_bonus_types, 0 is a valid value.
   */

  if ((_isNil(signUpBonusType) || signUpBonusType === CREDIT_CARDS_REWARD_TYPES.NO_REWARDS) && signUpBonus === 0) return 'N/A';
  return signUpBonus;
};
/**
 * Returns `valueTooltip` field to be used in the Intro Offer Quick Fact.
 * @param {object} offer - The CC offer.
 * @returns {string}
 */

export var getIntroOfferValueTooltip = function getIntroOfferValueTooltip(offer) {
  if (isDiscover(offer) || isChase(offer)) {
    return getSignUpBonusDetails(offer);
  }

  return getSignUpBonus(offer) ? getSignUpBonusDetails(offer) : null;
};
/**
 * Get the recommended credit score range text.
 * @param {object}  offer  The CC offer.
 * @return {object} - The credit score range & description.
 * @property {string} creditScoreRange
 * @property {string} creditScoreRangeDescription
 */

export var getRecommendedCreditScoreFormattedDetails = function getRecommendedCreditScoreFormattedDetails(offer) {
  var minCreditScoreIndex = getMinimumFICO(offer);
  var maxCreditScoreIndex = getMaximumFICO(offer);

  var maxCreditScoreRange = _capitalize(getMaxCreditScoreBucket(offer));

  var minCreditScoreRange = _capitalize(getMinCreditScoreBucket(offer));

  var creditScoreRange = minCreditScoreIndex === maxCreditScoreIndex ? minCreditScoreIndex : "".concat(minCreditScoreIndex, "-").concat(maxCreditScoreIndex);
  var creditScoreRangeDescription = maxCreditScoreRange === minCreditScoreRange ? minCreditScoreRange : "".concat(minCreditScoreRange, " - ").concat(maxCreditScoreRange);
  return {
    creditScoreRange: creditScoreRange,
    creditScoreRangeDescription: creditScoreRangeDescription
  };
};
/**
 * Get the reward type description.
 * @param   {Object}  offer  The CC offer.
 * @returns {string} - The reward rate description.
 */

export var getRewardRateDescription = function getRewardRateDescription(offer) {
  var rewardRateType = getRewardRateType(offer);
  var rewardType = getRewardType(offer);
  /**
   * Return `Cashback` denomination when reward rate type value is percent
   * with reward type as cash.
   */

  if (rewardRateType === CREDIT_CARDS_REWARD_RATE_TYPES.PERCENT && rewardType === CREDIT_CARDS_REWARD_TYPES.CASH) {
    return 'Cashback';
  }

  return CREDIT_CARDS_REWARD_TYPE_DESCRIPTIONS[rewardType] || null;
};